import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check, Zap, Globe, TrendingUp, Shield, Star, Sparkles, Lock, ChevronRight, Rocket, Earth } from 'lucide-react';
import Particles from './components/particles';
import { ActivityFeed } from './components/activity-feed';

const LogoImageUrl = new URL(
  'images/r.png?as=webp&width=250',
  import.meta.url
);
const JLogoImageUrl = new URL(
  'images/j-w.png?as=webp&width=250',
  import.meta.url
);
import './App.css';


export function App() {

  // const [activityMinimized, setActivityMinimized] = useState(true);
  const [currentActivity, setCurrentActivity] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [calcValues, setCalcValues] = useState({
    traffic: 5000,
    leadValue: 500,
    conversionRate: 2.5
  });

  // Calculator logic
  const trafficMultiplier = 25; // realtor.io gives 25x traffic boost
  const projectedTraffic = calcValues.traffic * trafficMultiplier;
  const monthlyLeads = projectedTraffic * (calcValues.conversionRate / 100);
  const monthlyRevenue = monthlyLeads * calcValues.leadValue;
  const annualRevenue = monthlyRevenue * 12;
  const domainCost = 5000000;
  const roiMonths = domainCost / monthlyRevenue;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentActivity((prev) => (prev + 1) % activities.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);  

  // const handleBuyNow = async () => {
  //   const stripe = await window.Stripe('pk_test_YOUR_PUBLISHABLE_KEY');
  //   const response = await fetch('YOUR_BACKEND_URL/create-checkout-session', {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify({ domain: 'realtor.io', price: 5000000 }),
  //   });
  //   const session = await response.json();
  //   const result = await stripe.redirectToCheckout({ sessionId: session.id });
  //   if (result.error) alert(result.error.message);
  // };

  const features = [
    { icon: Globe, title: 'Global Recognition', desc: 'Instantly recognized by millions worldwide' },
    { icon: TrendingUp, title: '100K+ Monthly Searches', desc: 'Massive organic traffic potential' },
    { icon: Shield, title: 'Ultimate Authority', desc: 'Industry-leading credibility built-in' },
    { icon: Zap, title: 'SEO Powerhouse', desc: 'Exact match domain advantage' },
  ];

  const stats = [
    { value: '100K+', label: 'Monthly Searches' },
    { value: '15+', label: 'Years Established' },
    { value: '$1B+', label: 'Industry Value' },
  ];

  const activities = [
    { name: 'Sarah M.', location: 'New York, NY', action: 'Requested information', time: '12 minutes ago', icon: '👤' },
    { name: 'Michael T.', location: 'Los Angeles, CA', action: 'Viewed domain details', time: '1 hour ago', icon: '👁️' },
    { name: 'Investment Group', location: 'Miami, FL', action: 'Made serious inquiry', time: '3 hours ago', icon: '💼' },
    { name: 'Jennifer K.', location: 'Austin, TX', action: 'Added to watchlist', time: '5 hours ago', icon: '⭐' },
    { name: 'Tech Ventures LLC', location: 'San Francisco, CA', action: 'Contacted specialist', time: '8 hours ago', icon: '🏢' },
  ];

  const faqs = [
    {
      question: "How does the purchase process work?",
      answer: "Once you complete payment through our secure Stripe checkout, we initiate the domain transfer immediately. You'll receive transfer authorization codes and step-by-step instructions via email. Our domain specialists are available 24/7 to guide you through the entire process, which typically takes 5-7 business days to complete."
    },
    {
      question: "Is the $627,000 USD price negotiable?",
      answer: "The price reflects the domain's exceptional value, market position, and revenue potential. realtor.io is a category-defining asset with 100K+ monthly searches and 15+ years of established brand recognition. While we occasionally consider serious offers from qualified buyers, the listed price represents fair market value for this premium digital asset."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major payment methods through Stripe, including credit cards, bank transfers (ACH/wire), and cryptocurrency. All transactions are processed securely with enterprise-grade encryption. For purchases over $1M, we can arrange custom payment plans or escrow services through Dan.com or Escrow.com."
    },
    {
      question: "Do you offer financing options?",
      answer: "Yes! We understand that premium domain acquisitions represent significant investments. We can arrange flexible financing terms for qualified buyers, including installment plans, lease-to-own agreements, and deferred payment structures. Contact our specialists to discuss custom financing options tailored to your needs."
    },
    {
      question: "What happens after I purchase the domain?",
      answer: "Upon payment confirmation, you'll receive: (1) Transfer authorization codes within 24 - 48 hours, (2) Complete DNS management access, (3) Dedicated support during the transition period. We ensure a seamless handoff with zero downtime."
    },
    {
      question: "Is this a secure transaction?",
      answer: "Absolutely. All transactions are protected by Stripe's industry-leading security, bank-level encryption, and escrow services. We're a verified domain marketplace with thousands of successful transfers. Your payment is held in escrow until the domain transfer is confirmed complete. We also offer buyer protection guarantees and full refund policies if transfer issues arise."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white overflow-hidden relative" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      <Particles className="absolute inset-0 pointer-events-none" quantity={150} ease={80} staticity={50} color="#6366f1" size={0.5} />

      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-slate-900/50">
        <div 
          className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-200 ease-out shadow-lg shadow-indigo-500/50"
          style={{ width: `${scrollProgress}%` }}
        >
          {/* Glowing tip */}
          <div className="absolute right-0 top-0 w-20 h-full bg-gradient-to-l from-white/40 to-transparent"></div>
        </div>
      </div>

      {/* Percentage Indicator (optional - shows on right side) */}
      {scrollProgress > 5 && (
        <div className="fixed top-4 right-2 z-50 px-3 py-1 bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-full text-xs font-semibold text-slate-300 mr-4">
          {Math.round(scrollProgress)}%
        </div>
      )}

      {/* Floating Activity Feed */}
      <ActivityFeed activities={activities} currentActivity={currentActivity} setCurrentActivity={setCurrentActivity} />

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Eyebrow */}
          <div className="flex flex-col justify-center items-center mb-8">
                     <div className="text-white items-center gap-6 flex self-center w-30 mx-auto mb-2"><svg viewBox="0 0 32 32" aria-hidden="true" preserveAspectRatio="xMinYMid meet" className="w-[32px]"><rect clipPath="url(#:R35dba:-clip)" className="h-8 transition-all duration-300 fill-white w-0 group-hover/logo:w-8"></rect><use href="#:R35dba:-path" className="stroke-white" fill="none" strokeWidth="1.5"></use><defs><path id=":R35dba:-path" d="M3.25 26v.75H7c1.305 0 2.384-.21 3.346-.627.96-.415 1.763-1.02 2.536-1.752.695-.657 1.39-1.443 2.152-2.306l.233-.263c.864-.975 1.843-2.068 3.071-3.266 1.209-1.18 2.881-1.786 4.621-1.786h5.791V5.25H25c-1.305 0-2.384.21-3.346.627-.96.415-1.763 1.02-2.536 1.751-.695.658-1.39 1.444-2.152 2.307l-.233.263c-.864.975-1.843 2.068-3.071 3.266-1.209 1.18-2.881 1.786-4.621 1.786H3.25V26Z"></path><clipPath id=":R35dba:-clip"><use href="#:R35dba:-path"></use></clipPath></defs></svg><svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 330 32" aria-hidden="true" fill="currentColor" className="scale-[1.3] transform h-8 block"><g id="Shogun_Logo" data-name="Shogun Logo"><text id="Shogun" className="select-none font-display text-[64px] font-bold transition-all hover:text-blue-600 text-white cls-1" x="0" y="32.827">Shogun.®</text></g></svg></div>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full backdrop-blur-sm">
              <Earth className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-semibold text-indigo-300">Premium Domains</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-none">
              Own The Most Valuable<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                Real Estate Domain
              </span>
            </h1>
            <p className="text-lg md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              <strong>realtor.io</strong> represents the pinnacle of digital real estate. Instant credibility, massive traffic, and unmatched brand recognition.
            </p>

            <img src={LogoImageUrl} className="mx-auto w-10" alt="REALTOR.IO" />

            {/* Price */}
            <div className="inline-flex items-baseline gap-3 mb-12">
              <span style={{ fontFamily: "Special Gothic Expanded One"}} className="text-5xl md:text-6xl lg:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                $627,000
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a
                // onClick={handleBuyNow}
                href="https://buy.stripe.com/7sY00aarSfum6Hxe6P7N600"
                className="group relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold border border-black/10 border-2 rounded-xl shadow-2xl shadow-indigo-500/50 hover:shadow-indigo-500/80 transform transition-all duration-300"
              >
                <span className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Secure Purchase Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
              <a
                href="mailto:j.c@realtor.io"
                className="px-8 py-4 border-2 border-indigo-500/50 text-indigo-300 font-semibold rounded-xl hover:bg-indigo-500/10 transition-all duration-300"
              >
                Talk to Specialist
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>Instant Transfer</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>Secure Escrow</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>100% Guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative z-10 py-12 border-y border-slate-800/50 bg-slate-900/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Why This Domain Changes Everything</h2>
            <p className="text-xl text-slate-400">The competitive advantages are undeniable</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="group p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="mb-6">
                    <div className="inline-flex p-3 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl border border-indigo-500/30">
                      <Icon className="w-6 h-6 text-indigo-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Calculate Your ROI</h2>
            <p className="text-xl text-slate-400">See how realtor.io could transform your business with a <strong>$5M</strong> seed investment.</p>
          </div>

          <style>{`
            input[type="range"] {
              -webkit-appearance: none;
              appearance: none;
              background: transparent;
              cursor: pointer;
            }

            input[type="range"]::-webkit-slider-track {
              background: linear-gradient(to right, #4f46e5, #7c3aed, #db2777);
              height: 8px;
              border-radius: 9999px;
            }

            input[type="range"]::-moz-range-track {
              background: linear-gradient(to right, #4f46e5, #7c3aed, #db2777);
              height: 8px;
              border-radius: 9999px;
            }

            input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: linear-gradient(135deg, #6366f1, #a855f7);
              border: 2px solid white;
              box-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
              cursor: pointer;
              margin-top: -6px;
            }

            input[type="range"]::-moz-range-thumb {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: linear-gradient(135deg, #6366f1, #a855f7);
              border: 2px solid white;
              box-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
              cursor: pointer;
            }

            input[type="range"]:hover::-webkit-slider-thumb {
              box-shadow: 0 0 15px rgba(99, 102, 241, 0.8);
              transform: scale(1.1);
            }

            input[type="range"]:hover::-moz-range-thumb {
              box-shadow: 0 0 15px rgba(99, 102, 241, 0.8);
              transform: scale(1.1);
            }
          `}</style>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {/* Input Side */}
            <div className="select-none bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-8">
              <h3 style={{ fontFamily: "Special Gothic Expanded One"}}  className="text-lg mb-6 text-slate-200">Your Current Metrics</h3>
              
              <div className="space-y-6">
                {/* Traffic Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-slate-400">Monthly Website Traffic</label>
                    <span className="text-sm font-semibold text-white">{calcValues.traffic.toLocaleString()}</span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="50000"
                    step="1000"
                    value={calcValues.traffic}
                    onChange={(e) => setCalcValues({...calcValues, traffic: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>1K</span>
                    <span>50K</span>
                  </div>
                </div>

                {/* Lead Value Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-slate-400">Average Lead Value</label>
                    <span className="text-sm font-semibold text-white">${calcValues.leadValue.toLocaleString()}</span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="2000"
                    step="50"
                    value={calcValues.leadValue}
                    onChange={(e) => setCalcValues({...calcValues, leadValue: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>$100</span>
                    <span>$2,000</span>
                  </div>
                </div>

                {/* Conversion Rate Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-slate-400">Conversion Rate</label>
                    <span className="text-sm font-semibold text-white">{calcValues.conversionRate}%</span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="10"
                    step="0.5"
                    value={calcValues.conversionRate}
                    onChange={(e) => setCalcValues({...calcValues, conversionRate: parseFloat(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>0.5%</span>
                    <span>10%</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                <p className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-indigo-300">Assumption:</strong> Premium domains like realtor.io typically generate 25x more organic traffic due to brand recognition and SEO authority.
                </p>
              </div>
            </div>

            {/* Results Side */}
            <div className="select-none bg-gradient-to-br from-indigo-600/20 to-purple-600/20 backdrop-blur-sm rounded-2xl border border-indigo-500/30 p-8">
              <h3 style={{ fontFamily: "Special Gothic Expanded One"}}  className="text-lg mb-6 text-white">With realtor.io</h3>
              
              <div className="space-y-6">
                {/* Projected Traffic */}
                <div className="p-4 bg-black/20 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-slate-400">Projected Monthly Traffic</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {projectedTraffic.toLocaleString()}
                  </div>
                  <div className="text-xs text-green-400 mt-1">
                    +{((trafficMultiplier - 1) * 100).toFixed(0)}% increase
                  </div>
                </div>

                {/* Monthly Revenue */}
                <div className="p-4 bg-black/20 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Sparkles className="w-5 h-5 text-indigo-400" />
                    <span className="text-sm text-slate-400">Projected Monthly Revenue</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    ${monthlyRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </div>
                </div>

                {/* Annual Revenue */}
                <div className="p-4 bg-black/20 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-slate-400">Projected Annual Revenue</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    ${(annualRevenue / 1000000).toFixed(1)}M
                  </div>
                </div>

                {/* ROI Timeline */}
                <div className="p-4 bg-black/20 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-slate-400">ROI Timeline</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {roiMonths.toFixed(1)} months
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    To recoup ${(domainCost / 1000000).toFixed(1)}M investment
                  </div>
                </div>
              </div>

              <a
                // onClick={handleBuyNow}
                href="https://buy.stripe.com/7sY00aarSfum6Hxe6P7N600"
                className="flex justify-center w-full mt-6 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow hover:shadow-md shadow-[rgba(0,0,0,0.3)] transform transition-all duration-300"
              >
                <span className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Secure Purchase Now
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Traffic Graph Widget */}
      <section className="relative z-10 py-20 px-6 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Traffic Trends & Market Demand</h2>
            <p className="text-xl text-slate-400">Consistent growth in search volume over the past 12 months</p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl border border-slate-700/50 p-8 md:p-12 backdrop-blur-sm">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">+127%</div>
                <div className="text-sm text-slate-400">Year over Year</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-indigo-400 mb-2">100K+</div>
                <div className="text-sm text-slate-400">Monthly Searches</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">8.2M</div>
                <div className="text-sm text-slate-400">Annual Volume</div>
              </div>
            </div>

            {/* Graph Area */}
            <div className="relative h-64 md:h-80">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-slate-500 pr-4">
                <span>120K</span>
                <span>90K</span>
                <span>60K</span>
                <span>30K</span>
                <span>0</span>
              </div>

              {/* Graph container */}
              <div className="ml-12 h-full relative">
                {/* Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="border-t border-slate-700/30"></div>
                  ))}
                </div>

                {/* SVG Graph - Line and Fill */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient id="graphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>

                  {/* Area fill */}
                  <path
                    d="M 0,100 L 0,75 Q 8.33,71 16.66,67 T 33.33,58 T 50,42 T 66.66,33 T 83.33,25 L 100,17 L 100,100 Z"
                    fill="url(#graphGradient)"
                  />

                  {/* Line */}
                  <path
                    d="M 0,75 Q 8.33,71 16.66,67 T 33.33,58 T 50,42 T 66.66,33 T 83.33,25 L 100,17"
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="0.8"
                    className="drop-shadow-lg"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.6))' }}
                  />
                </svg>

                {/* Data points - Separate layer with preserved aspect ratio */}
                {[
                  { x: 0, y: 75 },
                  { x: 16.66, y: 67 },
                  { x: 33.33, y: 58 },
                  { x: 50, y: 42 },
                  { x: 66.66, y: 33 },
                  { x: 83.33, y: 25 },
                  { x: 100, y: 17 }
                ].map((point, i) => (
                  <div
                    key={i}
                    className="absolute"
                    style={{
                      left: `${point.x}%`,
                      top: `${point.y}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div className="relative">
                      <div className="w-3 h-3 bg-indigo-500 rounded-full shadow-lg"></div>
                      <div className="absolute inset-0 w-5 h-5 -m-1 border-2 border-indigo-500 rounded-full opacity-30 animate-pulse"></div>
                    </div>
                  </div>
                ))}

                {/* X-axis labels */}
                <div className="absolute bottom-0 left-0 right-0 -mb-8 flex justify-between text-xs text-slate-500">
                  <span>Jan</span>
                  <span>Mar</span>
                  <span>May</span>
                  <span>Jul</span>
                  <span>Sep</span>
                  <span>Nov</span>
                  <span>Dec</span>
                </div>
              </div>
            </div>

            {/* Insight Badge */}
            <div className="mt-12 flex items-center justify-center gap-3 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
              <TrendingUp className="w-5 h-5 text-indigo-400" />
              <span className="text-sm text-slate-300">
                <strong className="text-indigo-300">Trending upward:</strong> Search interest has grown 127% year-over-year, indicating increasing market value
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Domain Details */}
      <section className="relative z-10 py-20 px-6 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
            <div className="mx-auto w-72">
              <div className="inline-flex items-baseline gap-3 mb-2">
                <span style={{ fontFamily: "Special Gothic Expanded One"}} className="text-3xl md:text-4xl lg:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-30% from-white via-purple-500 to-indigo-800">
                  realtor.io
                </span>
              </div>
            </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Domain Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Extension', value: '.IO' },
              { label: 'Length', value: '7 characters' },
              { label: 'Category', value: 'Real Estate' },
              { label: 'Status', value: 'Available', highlight: true },
            ].map((item, idx) => (
              <div key={idx} className="flex justify-between items-center p-5 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <span className="text-slate-400">{item.label}</span>
                <span className={`font-semibold ${item.highlight ? 'text-green-400' : 'text-white'}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="relative z-10 py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-slate-400">Everything you need to know about purchasing this domain</p>
          </div>

          <div className="space-y-1">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-4 py-3 flex items-center justify-between gap-4 text-left group hover:bg-slate-800/30 rounded-lg transition-colors"
                >
                  <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
                    {faq.question}
                  </span>
                  <div className={`flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}>
                    <ChevronRight className="w-3 h-3 text-slate-500 rotate-90" />
                  </div>
                </button>
                
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-4 pb-3 text-xs text-slate-400 leading-snug">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Still have questions CTA */}
          <div className="mt-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 blur-xl"></div>
            <div className="relative p-8 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl border border-indigo-500/30 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
                  <p className="text-sm text-slate-400">Our domain specialists are available 24/7 to help you</p>
                </div>
                <a
                  href="mailto:j.c@realtor.io"
                  className="group px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow shadow-black/70 transform transition-all duration-300 flex items-center gap-2"
                >
                  Get in Touch
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>


{/*      <div className="mx-auto max-w-4xl px-6 py-20">
        <div className="mb-10">
          <div style={{ fontFamily: "Special Gothic Expanded One"}} className="mb-8 text-3xl font-medium leading-normal sm:text-4xl sm:leading-relaxed">Key Signals <span className="font-sans font-light italic">for</span> <span className="">realtor.io</span></div>
          <div className="max-w-xl text-gray-400 text-lg">Unlock the potential of the real estate market with the captivating domain realtor.io.</div>
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="mr-4">
            <div className="mt-8 text-xl font-medium md:mt-0 md:mb-8 md:text-2xl">.IO is among the top 20 ranked extensions</div>
            <div className="text-gray-500">A ".io" domain is a top-level domain (TLD) originally assigned to the British Indian Ocean Territory.</div>
          </div>
          <div className="mr-4">
            <div className="mt-8 text-xl font-medium md:mt-0 md:mb-8 md:text-2xl">Matching Companies Found in Crunchbase</div>
            <div className="text-gray-500">Crunchbase is a valuable resource for startups, offering data and insights to propel their growth and success.</div>
          </div>
 
          <div className="mr-4">
            <div className="mt-8 text-xl font-medium md:mt-0 md:mb-8 md:text-2xl">Registered in 322 TLDs</div>
            <div className="text-gray-500">A single-word domain's value depends on its brandability, and traffic potential.</div>
          </div>
        </div>
      </div>*/}




      {/* Final CTA */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-3xl border border-indigo-500/30 backdrop-blur-sm">
            <Rocket className="w-12 h-12 mx-auto mb-6 text-indigo-400" />
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6">Ready to Dominate Real Estate?</h2>
            <p className="text-lg sm:text-xl text-slate-300 mb-8">
              Secure realtor.io today and own the most recognizable domain in the industry.
              The keyword <em>realtor</em> receives a significant amount of searches per month on Google®
            </p>
            <a
              // onClick={handleBuyNow}
              href="https://buy.stripe.com/7sY00aarSfum6Hxe6P7N600"
              className="inline-flex group px-6 sm:px-10 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl shadow hover:shadow-md shadow-black/50 transform transition-all duration-300"
            >
              <span className="flex items-center gap-3">
              {/*<img src={LogoImageUrl} className="mr-1 w-6" alt="REALTOR.IO" />*/}
                Buy for $627,000
                {/*                
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                */}              
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center mb-8">
            <p className="text-slate-500 text-sm mb-6">© 2025 <strong>SHOGUN.IO</strong>® Domain Marketplace. All rights reserved.</p>
            
            {/* Powered by Stripe Badge */}
            <div className="flex items-center gap-3 px-6 py-3 bg-slate-900/50 rounded-lg border border-slate-800/50">
              <span className="text-slate-400 text-sm">Powered by</span>
              <svg className="h-6" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#6772E5"/>
              </svg>
            </div>
          </div>

          {/* Legal */}
          <div className="max-w-4xl mx-auto space-y-4 text-xs text-slate-600 leading-relaxed">
            <p>
              <strong className="text-slate-500">Legal Disclaimer:</strong> This domain sale is subject to verification and approval. All transactions are processed through secure escrow services to protect both buyer and seller. The purchase price is payable in full upon acceptance of offer. Domain transfer will be initiated within 24-48 hours of payment confirmation and may take 5-7 business days to complete depending on registrar processing times.
            </p>
            <p>
              <strong className="text-slate-500">Purchase Terms:</strong> By clicking "Buy Now" you agree to purchase the domain "realtor.io" at the listed price of $627,000 USD. Payment is processed securely through Stripe. All sales are final and non-refundable once the domain transfer process has begun. The domain is sold "as-is" with all existing traffic, SEO value, and brand recognition included. Seller makes no warranties regarding future performance, traffic levels, or revenue potential.
            </p>
            <p>
              <strong className="text-slate-500">Transfer Process:</strong> Upon successful payment, you will receive transfer authorization codes and detailed instructions via email. Our domain specialists will guide you through the entire transfer process. Transfer fees charged by registrars (if any) are the responsibility of the buyer. Domain ownership will be transferred to the registrar account specified by the buyer during checkout.
            </p>
            <p className="text-slate-700 text-[10px]">
              This Site and the Services are available only to individuals or entities (“Users”) who can form legally binding contracts under applicable law. By using this Site or the Services, you represent and warrant that you are (i) at least eighteen (18) years of age, (ii) otherwise recognized as being able to form legally binding contracts under applicable law, and/or (iii) are not a person barred from purchasing or receiving the Services found under the laws of the United States or other applicable jurisdiction.
              <br /><br />
              For expired domain names purchased through your account, you agree that you are responsible for payment within forty-eight (48) hours of auction close for the successful bid amount plus the one (1) year renewal or transfer fee (from the end of the domain name’s previous registration period), plus ICANN fee, if applicable, or any valid payment method associated with the account, will be charged on the third day following the auction close. If we are unable to collect payment, you may lose the rights to purchase the domain name.
            </p>
          </div>

          <div className="max-w-4xl mx-auto my-2 flex gap-2 items-center">
            <img src={JLogoImageUrl} className="w-10" alt="built by JC®" />
          </div>
        </div>
      </footer>
    </div>
  );
}